var m = require('mithril');
var prop = require('mithril/stream');
var heading = require('../../components/heading');
var buttonElement = require('../../components/button');
var flash = require('../../components/flash');
var loaders = require('../../layout/components/loaders');
var header = require('../../layout/components/header');
var requestStatusHandler = require('../../layout/components/requestStatusHandler');
var RequestStatus = require('../../layout/enums/RequestStatus');
var Model = require('../../libs/Model');
var ToggleSwitch = require('../../components/toggleSwitch');
var UserProfileForm = Object.assign({}, require('../../../../../models/users/user'), Model);
var UserAdminForm = Object.assign({}, require('../../../../../models/users/userAdmin'), Model);
UserProfileForm.fields = Object.assign({}, UserProfileForm.fields);
delete UserProfileForm.fields.username;
delete UserProfileForm.fields.useUsaUnits;
var userPassword = require('../models/userPassword');
var session = require('../models/session');
var tabs = require('../../components/tabs');
var groupPermissionModal = require('../../locations/components/editGroupPermissionsModal');
var mapUtils = require('../../maps/mapUtils');
var alarmsForUser = require('../../alarms/alarmsForUser');

const setRoomClassTree = mapUtils.setRoomClassTree;
const isLocationClass = mapUtils.isLocationClass;
const isRoomClass = mapUtils.isRoomClass;
const USER_EDIT_TAB_ROUTE_KEY = 'tab';
const USER_ROLE_FIELDS = [
  { name: 'isSuperuser', label: 'Superuser' },
  { name: 'isReportAdmin', label: 'Report Admin' },
  { name: 'isDeveloper', label: 'Developer' },
  { name: 'isApiUser', label: 'API User' },
  { name: 'isCalibrationSuperuser', label: 'Calibration Superuser' }
];

var roomClassTreePromise = null;

function ensureRoomClassTreeLoaded() {
  if (!roomClassTreePromise) {
    roomClassTreePromise = loaders.requestWithLoader({
      loader: 'background',
      url: '/api/v1/roomclasstree',
      method: 'GET'
    })
      .then(function (tree) {
        setRoomClassTree(tree);
      })
      .catch(function () {});
  }
  return roomClassTreePromise;
}

// Natural sort helpers (number-aware, case-insensitive)
function tokenize(str) {
  const input = String(str || '');
  return input.split(/(\d+)/).map((s) => (isNaN(s) ? s.toLowerCase() : parseInt(s, 10)));
}
function naturalCompareStrings(a, b) {
  const ax = tokenize(a);
  const bx = tokenize(b);
  for (let i = 0; i < Math.max(ax.length, bx.length); i++) {
    const av = ax[i];
    const bv = bx[i];
    if (av === undefined) return -1;
    if (bv === undefined) return 1;
    if (typeof av === 'number' && typeof bv === 'number') {
      if (av !== bv) return av - bv;
    } else {
      const as = String(av);
      const bs = String(bv);
      if (as !== bs) return as.localeCompare(bs, undefined, { sensitivity: 'base' });
    }
  }
  return 0;
}

function normalizeRid(rid) {
  return rid ? rid.toString().replace(/^#/, '') : '';
}

function normalizeRoleFlag(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    var lower = value.trim().toLowerCase();
    if (['1', 'true', 'yes', 'on'].includes(lower)) return true;
    if (['0', 'false', 'no', 'off'].includes(lower)) return false;
  }
  return false;
}

function setRoleFlag(fieldName, enabled) {
  if (UserAdminForm.form && UserAdminForm.form[fieldName]) {
    UserAdminForm.form[fieldName](enabled ? 1 : 0);
  }
}

function roleSwitchRow(field) {
  var formProp = UserAdminForm.form && UserAdminForm.form[field.name];
  if (!formProp) return null;
  var inputId = 'user-role-' + field.name + '-toggle';
  var enabled = normalizeRoleFlag(formProp());

  return m('div.mb3', [
    m('div.flex.items-center.justify-between', [
      m('label', { for: inputId }, field.label),
      m(ToggleSwitch, {
        id: inputId,
        checked: enabled,
        ariaLabel: field.label,
        onchange: function () {
          setRoleFlag(field.name, !enabled);
        }
      })
    ])
  ]);
}

function userRoleSwitchesView() {
  return m('form', { role: 'form' }, [
    UserAdminForm.errorView(),
    USER_ROLE_FIELDS.map(roleSwitchRow)
  ]);
}

var page = {};

page.oninit = function () {
  var ctrl = this;
  header.vm.activeTab('Users');
  document.title = 'nimrod | Edit User';
  ctrl.successFlash = prop([]);
  ctrl.errorFlash = prop([]);
  ctrl.loading = true;
  ctrl.requestStatus = prop(RequestStatus.PENDING);
  ctrl.requestStatusCode = prop();
  ctrl.groupPermissions = [];
  ctrl.groupPermissionsLoading = false;
  ctrl.rid = normalizeRid(m.route.param('rid'));

  if (!session.isSuperuser()) {
    ctrl.loading = false;
    ctrl.requestStatusCode(403);
    ctrl.requestStatus(RequestStatus.ERROR);
    return;
  }

  ctrl.setFlash = function (type, messages) {
    var propRef = type === 'success' ? ctrl.successFlash : ctrl.errorFlash;
    propRef(messages || []);
  };

  ctrl.loadUser = function (options) {
    var requestedRid = ctrl.rid;
    ctrl.loading = true;
    ctrl.requestStatus(RequestStatus.PENDING);
    ctrl.requestStatusCode();
    if (!(options && options.preserveSuccess)) {
      ctrl.setFlash('success', []);
    }
    ctrl.setFlash('error', []);
    return Promise.all([
      ensureRoomClassTreeLoaded(),
      loaders.requestWithLoader({
        method: 'GET',
        url: '/api/v1/user/' + requestedRid
      })
    ])
      .then(function (results) {
        if (requestedRid !== ctrl.rid) return;
        var record = results[1];
        ctrl.user = record;
        UserProfileForm.initForm(record);
        UserAdminForm.initForm(record);
        userPassword.initForm(record);
        document.title = 'nimrod | Edit ' + (record.firstName && record.lastName ? record.firstName + ' ' + record.lastName : 'User');
        ctrl.loadGroupPermissions();
        ctrl.loading = false;
        ctrl.requestStatus(RequestStatus.SUCCESS);
        m.redraw();
      })
      .catch(function (error) {
        if (requestedRid !== ctrl.rid) return;
        ctrl.loading = false;
        ctrl.requestStatusCode(error && (error.status || error.code || (error.response && error.response.status)));
        ctrl.requestStatus(RequestStatus.ERROR);
        if (!ctrl.requestStatusCode()) {
          ctrl.setFlash('error', ['Unable to load user']);
        }
        m.redraw();
      });
  };

  ctrl.saveUser = function () {
    ctrl.setFlash('success', []);
    ctrl.setFlash('error', []);
    return UserProfileForm.save()
      .then(function () {
        ctrl.setFlash('success', ['User updated']);
        ctrl.loadUser({ preserveSuccess: true });
      })
      .catch(function () {
        if (!UserProfileForm.errors || UserProfileForm.errors.length === 0) {
          ctrl.setFlash('error', ['Unable to update user']);
        }
      });
  };

  ctrl.saveAdminSettings = function () {
    ctrl.setFlash('success', []);
    ctrl.setFlash('error', []);
    return UserAdminForm.save()
      .then(function () {
        ctrl.setFlash('success', ['Permissions updated']);
        ctrl.loadUser({ preserveSuccess: true });
      })
      .catch(function () {
        if (!UserAdminForm.errors || UserAdminForm.errors.length === 0) {
          ctrl.setFlash('error', ['Unable to update permissions']);
        }
      });
  };

  ctrl.resetPassword = function () {
    ctrl.setFlash('success', []);
    ctrl.setFlash('error', []);
    return userPassword.save()
      .then(function () {
        ctrl.setFlash('success', ['Password reset']);
        userPassword.initForm(ctrl.user);
      })
      .catch(function () {
        if (!userPassword.errors || userPassword.errors.length === 0) {
          ctrl.setFlash('error', ['Unable to reset password']);
        }
        return Promise.reject(userPassword.errors);
      });
  };

  ctrl.loadGroupPermissions = function () {
    const groups = Array.isArray(ctrl.user && ctrl.user.groups) ? ctrl.user.groups : [];
    if (groups.length === 0) {
      ctrl.groupPermissions = [];
      ctrl.groupPermissionsLoading = false;
      return;
    }

    ctrl.groupPermissionsLoading = true;
    Promise.all(groups.map(function (g) {
      const rid = normalizeRid(g['@rid']);
      if (!rid) return null;
      return loaders.requestWithLoader({
        url: '/api/v1/group/' + rid,
        method: 'GET'
      }).then(function (result) {
        return {
          '@rid': g['@rid'],
          name: result.name || g.name,
          permissions: result.permissions || [],
          raw: result
        };
      }).catch(function () { return null; });
    })).then(function (results) {
      ctrl.groupPermissions = (results || []).filter(Boolean);
      ctrl.groupPermissionsLoading = false;
      m.redraw();
    }).catch(function () {
      ctrl.groupPermissionsLoading = false;
      m.redraw();
    });
  };

  ctrl.loadUser();
};

page.onbeforeupdate = function () {
  var ctrl = this;
  var nextRid = normalizeRid(m.route.param('rid'));
  if (!nextRid || nextRid === ctrl.rid) return true;

  ctrl.rid = nextRid;
  ctrl.user = null;
  ctrl.groupPermissions = [];
  ctrl.groupPermissionsLoading = false;
  ctrl.loadUser();
  return true;
};

page.view = function () {
  var ctrl = this;

  if (ctrl.requestStatus() !== RequestStatus.SUCCESS || !ctrl.user) {
    return m(requestStatusHandler, {
      requestStatus: ctrl.requestStatus,
      requestStatusCode: ctrl.requestStatusCode,
    });
  }

  var headingLabel = ctrl.user && ctrl.user.firstName && ctrl.user.lastName
    ? `Edit ${ctrl.user.firstName} ${ctrl.user.lastName}`
    : 'Edit User';
  var tabSections = {
    Profile: [profileTab(ctrl)],
    Password: [passwordTab(ctrl)],
    'Alarm List': [alarmsForUser, { rid: ctrl.rid, embedded: true, headingLabel: 'Alarm List', pathDisplayOptions: { pathMode: 'locationEquipment', showNodeClass: false, omitMissingPath: true } }],
    Permissions: [permissionsTab(ctrl)]
  };

  return m('div.mh3.mb5', [
    m('div.v-btm.bb.b--mid-gray.bw1.f-subheadline.mb3.pb2.display.mb3.flex.justify-between.items-center', [
      m('div', [
        m('div.mr2.dib-ns', [
          m(m.route.Link, { href: '/users' }, 'Users'),
          ' / ',
          m('div.mr2.dib-ns', headingLabel)
        ])
      ])
    ]),
    m(flash, { items: ctrl.errorFlash(), bg: 'bg-red' }),
    m(flash, { items: ctrl.successFlash(), bg: 'bg-green' }),
    m(tabs, {
      tabs: tabSections,
      route: USER_EDIT_TAB_ROUTE_KEY,
      onChange: function () {
        ctrl.setFlash('success', []);
        ctrl.setFlash('error', []);
      }
    })
  ]);
};

function profileTab(ctrl) {
  return {
    view: function () {
      return m('div', {
        oninput: function () { ctrl.setFlash('success', []); ctrl.setFlash('error', []); }
      }, [
        m(UserProfileForm),
        m('div.mt4', [
          m(buttonElement, {
            label: 'Submit',
            class: 'green',
            func: ctrl.saveUser
          })
        ])
      ]);
    }
  };
}

function passwordTab(ctrl) {
  return {
    view: function () {
      return m('div', {
        oninput: function () { ctrl.setFlash('success', []); ctrl.setFlash('error', []); }
      }, [
        m(userPassword),
        m('div.mt3', [
          m(buttonElement, {
            label: 'Reset Password',
            func: ctrl.resetPassword
          })
        ])
      ]);
    }
  };
}

function permissionsTab(ctrl) {
  return {
    view: function () {
      const hasAnyPermission = function (edge) {
        return Object.keys(edge || {}).some(function (k) {
          if (['out', 'in', '@rid', '@class'].includes(k)) return false;
          const v = edge[k];
          return v === 1 || v === true || v === '1';
        });
      };

      const groupPermissionCount = function (groupData) {
        const permissions = Array.isArray(groupData && groupData.permissions) ? groupData.permissions : [];
        return permissions.filter(hasAnyPermission).length;
      };

      const sortedGroups = ctrl.groupPermissions.slice().sort(function (a, b) {
        return groupPermissionCount(b) - groupPermissionCount(a);
      });

      return m('div.mt2', [
        m('div', {
          oninput: function () { ctrl.setFlash('success', []); ctrl.setFlash('error', []); },
          onchange: function () { ctrl.setFlash('success', []); ctrl.setFlash('error', []); }
        }, [
          userRoleSwitchesView(),
          m('div.mt3', [
            m(buttonElement, {
              label: 'Save',
              class: 'green',
              func: ctrl.saveAdminSettings
            })
          ])
        ]),
        m(heading, { label: 'Access Permissions By Group', class: 'f1 mb3 mt4' }),
        ctrl.groupPermissionsLoading ?
          m('div.flex.justify-center.items-center', [
            m('img', {
              src: '/images/loading-bars.svg',
              style: { width: '64px', height: '64px' },
              alt: 'Loading chart data'
            })
          ])
          :
          null,
        (!ctrl.groupPermissionsLoading && ctrl.groupPermissions.length === 0) ?
          m('div.gray', 'No groups or permissions found for this user.')
          :
          sortedGroups.map(function (groupData) {
            const permissions = (groupData.permissions || []);

            const buildScopeString = function (edge) {
              const isTrue = function (v) { return v === 1 || v === true || v === '1'; };
              const hasAllRead = isTrue(edge.read);
              const hasAllWrite = isTrue(edge.write);
              if (hasAllRead || hasAllWrite) {
                var parts = [];
                if (hasAllRead) parts.push('R');
                if (hasAllWrite) parts.push('W');
                return "All (" + parts.join(' + ') + ")";
              }
              var readCount = 0;
              var writeCount = 0;
              Object.keys(edge || {}).forEach(function (key) {
                if (['out', 'in', '@rid', '@class'].includes(key)) return;
                if (key.startsWith('parent')) return;
                var val = edge[key];
                if (!isTrue(val)) return;
                if (key.endsWith('Read')) readCount++;
                if (key.endsWith('Write')) writeCount++;
              });
              return "Read: " + readCount + ", Write: " + writeCount;
            };

            const linkFor = function (edge) {
              const out = edge.out || {};
              const rid = (out['@rid'] || '').toString();
              const id = rid.startsWith('#') ? rid.slice(1) : rid;
              const klass = (out['@class'] || '').toString();
              if (klass === 'Dashboard') {
                return { href: '/dashboard/' + id, type: 'Dashboard' };
              } else if (klass === 'ReportFolder') {
                return { href: '/reportfolder/' + id + '/permissions', type: 'Report Folder' };
              } else if (isLocationClass(klass)) {
                return { href: '/location/' + id, type: 'Location' };
              } else {
                return { href: '/equipment/' + klass.toLowerCase() + '/' + id, type: 'Equipment' };
              }
            };

            const labelForPath = function (edge) {
              const out = edge.out || {};
              const pathArr = Array.isArray(out.path) ? out.path : [];
              const nodes = pathArr.filter(function (n) { return !(n && n.name === 'root' && n['@class'] === 'Dashboard'); });
              if (!nodes.length) return (out.name || out['@class'] || '').toString();
              const toLabel = function (node) {
                const klass = (node['@class'] || '').toString();
                if (node.name === 'root') return 'Home';
                if (isRoomClass(klass)) return (node.name + ' ' + (node.roomNumber || '')).trim();
                return node.name || klass;
              };
              return nodes.map(toLabel).join(' / ');
            };

            const dataToShow = permissions
              .filter(hasAnyPermission)
              .sort(function (a, b) { return naturalCompareStrings(labelForPath(a), labelForPath(b)); });

            const count = permissions.filter(hasAnyPermission).length;

            return m('div.mb3.ba.bw1.pa3', [
              m('div.flex.items-center.mb2.f3', [
                m('div.b.mr2', m(m.route.Link, { href: '/group/' + (groupData['@rid'] ? groupData['@rid'].slice(1) : '') }, groupData.name || 'Group')),
                m('div.gray', `Permissions - ${count}`)
              ]),
              dataToShow.length === 0 ?
                m('div.gray', 'No active permissions for this group.')
                :
                m('table.f4.mb3.mt1.w-100', [
                  m('colgroup', [
                    m('col', { style: { width: '50%' } }),
                    m('col', { style: { width: '30%' } }),
                    m('col', { style: { width: '20%' } })
                  ]),
                  m('thead', [
                    m('tr', [
                      m('th.ph0.tl.w-50', 'Path'),
                      m('th.ph0.tl.w-30', 'Type'),
                      m('th.ph0.tl.w-20', 'Scope'),
                    ])
                  ]),
                  m('tbody', [
                    dataToShow.map(function (edge) {
                      const out = edge.out || {};
                      const ref = linkFor(edge);
                      const scopeLabel = buildScopeString(edge);
                      const klass = (out['@class'] || '').toString();
                      const ridStr = (out['@rid'] || '').toString();
                      const id = ridStr.startsWith('#') ? ridStr.slice(1) : ridStr;
                      const scopeClick = function () {
                        if (klass === 'Dashboard') {
                          m.route.set('/dashboard/' + id + '/permissions');
                          return;
                        }
                        if (klass === 'ReportFolder') {
                          m.route.set('/reportfolder/' + id + '/permissions');
                          return;
                        }
                        if (!isLocationClass(klass)) {
                          m.route.set('/equipment/' + klass.toLowerCase() + '/' + id + '/permissions');
                          return;
                        }
                        edge.in = { '@rid': groupData['@rid'], name: groupData.name };
                        groupPermissionModal.editDialogue(edge, { submitCallback: ctrl.loadUser });
                      };
                      const pathArr = Array.isArray(out.path) ? out.path : [];
                      const nodes = pathArr.filter(function (n) { return !(n && n.name === 'root' && n['@class'] === 'Dashboard'); });

                      const renderPath = function () {
                        if (!nodes.length) {
                          const label = out.name || out['@class'] || ref.href;
                          return m(m.route.Link, { href: ref.href, class: 'truncate' }, label);
                        }
                        return m('div.flex.flex-wrap', nodes.map(function (node, idx) {
                          const klass = (node['@class'] || '').toString();
                          const ridStr = (node['@rid'] || '').toString();
                          const id = ridStr.startsWith('#') ? ridStr.slice(1) : ridStr;
                          const isLocation = isLocationClass(klass);
                          const label = node.name === 'root'
                            ? 'Home'
                            : isRoomClass(klass)
                              ? (node.name + ' ' + (node.roomNumber || '')).trim()
                              : (node.name || klass);
                          const nodeHref =
                            klass === 'Dashboard' ? '/dashboard/' + id :
                              klass === 'ReportFolder' ? '/reportfolder/' + id :
                              isLocation ? '/location/' + id :
                                '/equipment/' + klass.toLowerCase() + '/' + id;
                          return m('div.mr1', [
                            m(m.route.Link, { class: 'mr1', href: nodeHref }, label),
                            idx < nodes.length - 1 ? '/' : ''
                          ]);
                        }));
                      };
``
                      return m('tr', [
                        m('td.tl.w-50', renderPath()),
                        m('td.tl.w-30.nowrap', ref.type),
                        m('td.tl.w-20', [
                          session.user().isSuperuser == true || session.user().isSuperuser == 1 ?
                            m('a.blue.pointer', { onclick: scopeClick }, scopeLabel || '—') :
                            m('span', scopeLabel || '—')
                        ]),
                      ]);
                    })
                  ])
                ])
            ]);
          })
      ]);
    }
  };
}

module.exports = page;
